import _ from "lodash";
import { API_ROUTES } from "@/constants/apiRoutes";
import { httpRequestServer } from "@/services/httpRequestServer";
import { filterService } from "@/services/filter";
import { useTopbarFilterStore } from "@/store/topbarFilterStore";
import { useUserProfileStore } from "@/store/userProfileStore";
import { 
  IQuickFilterOptions, 
  IQuickFilterFolderOptions,
  IQuickFilterMoveOptions,
  IQuickFilterResponse, 
  IQuickFilterFolderResponse,
  IQuickFilterItemsResponse,
  IQuickFilterViewModel,
  IQuickFilterItemsViewModel, 
  } from "@/models/quickFilter.model";
import { FeatureName } from "@/models/userProfile.model";
import { IFilter } from "@/models/filter.model";

export const quickFilterDataService = {
  upsertAsync: async (options: IQuickFilterOptions) : Promise<IQuickFilterResponse> => {
    return await httpRequestServer.post(`${API_ROUTES.QUICK_FILTERS}/upsert`, options);
  },

  moveAsync: async (options: IQuickFilterMoveOptions) : Promise<IQuickFilterResponse> => {
    return await httpRequestServer.post(`${API_ROUTES.QUICK_FILTERS}/move`, options);
  },

  deleteAsync: async (id: string) : Promise<string> => {
    return await httpRequestServer.delete(`${API_ROUTES.QUICK_FILTERS}/delete?id=${id}`);
  },

  upsertFolderAsync: async (options: IQuickFilterFolderOptions) : Promise<IQuickFilterFolderResponse> => {
    return await httpRequestServer.post(`${API_ROUTES.QUICK_FILTERS}/upsert-folder`, options);
  },

  deleteFolderAsync: async (id: string) : Promise<string> => {
    return await httpRequestServer.delete(`${API_ROUTES.QUICK_FILTERS}/delete-folder?id=${id}`);
  },

  getQuickFiltersAsync: async () : Promise<IQuickFilterViewModel[]> => {
    let quickFilters =  await httpRequestServer.post(`${API_ROUTES.QUICK_FILTERS}/quick-filters`);
    return await transformQuickFilterResponsesToViewModelsAsync(quickFilters);
  },

  getQuickFilterItemsAsync: async () : Promise<IQuickFilterItemsViewModel> => {
    let quickFilterItemsResponse: IQuickFilterItemsResponse =  await httpRequestServer.post(`${API_ROUTES.QUICK_FILTERS}/items`);

    return {
      filters: await transformQuickFilterResponsesToViewModelsAsync(quickFilterItemsResponse.filters),
      folders: _.sortBy(quickFilterItemsResponse.folders, [item => item.name.toLowerCase()])
    };
  }
};

const transformQuickFilterResponsesToViewModelsAsync = async (quickFilters: IQuickFilterResponse[]): Promise<IQuickFilterViewModel[]> => {
  if (!quickFilters || !quickFilters.length) {
    return [];
  }

  const filterData = await useTopbarFilterStore().filterDataAsync;
  if (!filterData) {
    return [];
  }

  quickFilters = _.sortBy(quickFilters, [item => item.name.toLowerCase()]);
  const isObjectionsFeatureEnabled = useUserProfileStore().hasFeature(FeatureName.objectionsTab);
  const isScorecardsFeatureEnabled = useUserProfileStore().hasFeature(FeatureName.scoringScorecardsPage);

  function orderFilter(filter: IFilter): IFilter {
    return {
      campaignIds: filter.campaignIds,
      teamIds: filter.teamIds,
      userIds: filter.userIds,
      queueIds: filter.queueIds,
      resultCodes: filter.resultCodes,
      recordingLength: filter.recordingLength,
      callDirections: filter.callDirections,
      lists: filter.lists,
      daysOfWeek: filter.daysOfWeek,
      shortCalls: filter.shortCalls,
      transcriptSearch: filter.transcriptSearch,
      isScored: filter.isScored,
      scorecardFilter: filter.scorecardFilter,
      isSuccess: filter.isSuccess,
      firstName: filter.firstName,
      lastName: filter.lastName,
      phoneNumber: filter.phoneNumber,
      postalCode: filter.postalCode,
      referenceId: filter.referenceId,
      topics: filter.topics,
      overallSentiments: filter.overallSentiments,
      peakSentiments: filter.peakSentiments,
      endSentiments: filter.endSentiments,
      objections: filter.objections,
      dataSupplierIds: filter.dataSupplierIds
    };
  }
  return quickFilters.map((quickFilter: IQuickFilterResponse) => {
    const orderedFilter = orderFilter(quickFilter.data);
    return {  
      id: quickFilter.id,
      name: quickFilter.name,
      folderId: quickFilter.folderId,
      filter: orderedFilter, 
      chips: filterService.getChipsForFilter(
        quickFilter.data, 
        filterData, 
        isObjectionsFeatureEnabled,
        isScorecardsFeatureEnabled)
    };
  });
};